import React, { useMemo, useState } from 'react';
import {
  Accordion, AccordionDetails, AccordionSummary, Alert, Box, Chip, Divider,
  Paper, Stack, Table, TableBody, TableCell, TableHead, TableRow, ToggleButton,
  ToggleButtonGroup, Tooltip, Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

import { SUPERFICIE } from './estilo';

// Os quatro tipos de evento. O MOVED é o motivo do produto existir: sem ele,
// "o vizinho trocou de interface" apareceria como REMOVED + ADDED, e o operador
// teria que juntar os dois na cabeça às 3h da manhã.
const EVENTO = {
  MOVED: { sym: '⇄', cor: '#7c3aed', label: 'Migrou', desc: 'Mesma entidade, lugar diferente' },
  REMOVED: { sym: '−', cor: '#c0392b', label: 'Sumiu', desc: 'Existia antes, não existe depois' },
  ADDED: { sym: '+', cor: '#16a085', label: 'Apareceu', desc: 'Não existia antes' },
  CHANGED: { sym: '~', cor: '#d97706', label: 'Mudou', desc: 'Mesmo lugar, algum atributo diferente' },
};

const CAT_COLOR = {
  OSPF: '#9b59b6', BGP: '#e67e22', ISIS: '#2980b9', MPLS: '#2dd4bf',
  INTERFACE: '#8e44ad', L2: '#3498db', HW: '#c0392b', OUTROS: '#7f8c8d',
};

const ORDEM_EVENTO = ['MOVED', 'REMOVED', 'CHANGED', 'ADDED'];


function fmtValor(v) {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'boolean') return v ? 'sim' : 'não';
  return String(v);
}

/** Uma linha de evento. */
function Evento({ ev, hostNome }) {
  const cfg = EVENTO[ev.event] || EVENTO.CHANGED;
  const chave = (ev.entity_key_parts || []).filter(Boolean).join(' · ');
  const mudancas = Object.entries(ev.changes || {});

  return (
    <Box sx={{ py: 1, px: 1.5, borderLeft: `3px solid ${cfg.cor}`, mb: 0.75, bgcolor: '#fafafa' }}>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
        <Tooltip title={cfg.desc}>
          <Box component="span" sx={{ color: cfg.cor, fontWeight: 700, fontSize: 18, width: 18 }}>
            {cfg.sym}
          </Box>
        </Tooltip>
        <Chip
          size="small"
          label={ev.entity_type}
          sx={{ bgcolor: CAT_COLOR[ev.category] || CAT_COLOR.OUTROS, color: '#fff', height: 20 }}
        />
        <Typography sx={{ fontFamily: 'monospace', fontSize: 13, fontWeight: 600 }}>
          {chave}
        </Typography>
        {hostNome && (
          <Typography variant="caption" sx={{ color: '#64748b' }}>
            em {hostNome}
          </Typography>
        )}
      </Stack>

      {/* O MOVED merece destaque visual próprio: é a informação mais acionável
          da tela, e escondê-la numa tabela de atributos desperdiçaria o achado. */}
      {ev.event === 'MOVED' && (
        <Box sx={{ mt: 0.75, ml: 3.5, fontFamily: 'monospace', fontSize: 13 }}>
          <Box component="span" sx={{ color: '#c0392b' }}>{ev.location_before || '—'}</Box>
          <Box component="span" sx={{ mx: 1, color: '#7c3aed', fontWeight: 700 }}>→</Box>
          <Box component="span" sx={{ color: '#16a085' }}>{ev.location_after || '—'}</Box>
        </Box>
      )}

      {mudancas.length > 0 && (
        <Table size="small" sx={{ mt: 0.75, ml: 3.5, width: 'auto' }}>
          <TableBody>
            {mudancas.map(([campo, val]) => (
              <TableRow key={campo}>
                <TableCell sx={{ border: 0, py: 0.25, pl: 0, fontSize: 12, color: '#64748b' }}>
                  {campo}
                </TableCell>
                <TableCell sx={{ border: 0, py: 0.25, fontFamily: 'monospace', fontSize: 12, color: '#c0392b' }}>
                  {fmtValor(val.before)}
                </TableCell>
                <TableCell sx={{ border: 0, py: 0.25, fontSize: 12 }}>→</TableCell>
                <TableCell sx={{ border: 0, py: 0.25, fontFamily: 'monospace', fontSize: 12, color: '#16a085' }}>
                  {fmtValor(val.after)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Box>
  );
}

/**
 * Painel de comparação antes × depois.
 *
 * Consome GET /admin/hermes/snapshot/run/{id}/compare, que já devolve os eventos
 * agregados por tipo, categoria e host — a tela não recalcula nada.
 */
export default function CompareEventos({
  dados, hostsPorId = {}, busca = '', rotuloAntes, rotuloDepois,
}) {
  // O ID do run e global e cresce entre sessoes: a segunda coleta de uma sessao
  // nova aparecia como "#8". Quem opera conta dentro da janela — primeira,
  // segunda, terceira. O rotulo vem pronto de quem conhece a sessao; o ID cru so
  // sobra quando esta tela e usada fora dela.
  const antes = rotuloAntes || `coleta #${dados?.before_run_id}`;
  const depois = rotuloDepois || `coleta #${dados?.after_run_id}`;
  const [filtroEvento, setFiltroEvento] = useState(null);
  const [filtroCategoria, setFiltroCategoria] = useState(null);

  const porHost = dados?.per_host || [];

  const hostsFiltrados = useMemo(() => {
    const termo = (busca || '').trim().toLowerCase();
    if (!filtroEvento && !filtroCategoria && !termo) return porHost;
    return porHost
      .map((h) => ({
        ...h,
        events: h.events.filter((e) => {
          if (filtroEvento && e.event !== filtroEvento) return false;
          if (filtroCategoria && e.category !== filtroCategoria) return false;
          if (!termo) return true;
          // Busca no que o operador enxerga: tipo, chave e valores mudados.
          const alvo = [
            e.entity_type,
            ...(e.entity_key_parts || []),
            e.location_before, e.location_after,
            ...Object.entries(e.changes || {}).flat(2).map(String),
            hostsPorId[h.host_id]?.host_name,
          ].filter(Boolean).join(' ').toLowerCase();
          return alvo.includes(termo);
        }),
      }))
      .filter((h) => h.events.length > 0);
  }, [porHost, filtroEvento, filtroCategoria, busca, hostsPorId]);

  if (!dados) return null;

  // Escopo real da comparação.
  //
  // Um host que não respondeu não tem entidade em nenhum dos dois lados, então
  // ele não gera evento — e a comparação sai "limpa" sem que nada garanta que
  // ele está bem. Afirmar "nenhuma mudança" sem dizer o tamanho da amostra é o
  // erro mais perigoso que esta tela pode cometer: o operador encerra a janela
  // achando que conferiu um equipamento que sequer foi lido.
  const nomes = (ids) =>
    (ids || []).map((id) => hostsPorId[id]?.host_name || `host ${id}`).join(', ');

  const avisoCobertura = (
    <>
      {dados.partial && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          <Typography sx={{ fontWeight: 600 }}>
            Comparação parcial: {dados.hosts_compared} de {dados.hosts_expected} host(s)
          </Typography>
          <Typography variant="body2">
            {nomes(dados.hosts_missing)} não respondeu em uma das coletas e ficou de
            fora. O que aparece abaixo não diz nada sobre{' '}
            {dados.hosts_missing?.length > 1 ? 'esses equipamentos' : 'esse equipamento'}.
          </Typography>
        </Alert>
      )}

      {/* Diferente de não responder: aqui o equipamento foi coletado e o texto
          cru está guardado — só não há parser que transforme aquilo em algo
          comparável. Tratar os dois como o mesmo alarme faria o operador
          desconfiar de coleta que funcionou. */}
      {!!dados.hosts_unparsed?.length && (
        <Alert severity="info" sx={{ mt: 2 }}>
          <Typography variant="body2">
            {nomes(dados.hosts_unparsed)} foi coletado, mas nenhum comando dele tem
            parser — então não entra na comparação estruturada. A saída bruta está
            guardada e pode ser vista no histórico.
          </Typography>
        </Alert>
      )}
    </>
  );

  // Zero evento é o resultado BOM de uma manutenção. Precisa ser afirmativo, não
  // uma tela vazia que o operador confunde com "ainda não comparei".
  if (dados.clean) {
    return (
      <>
        {avisoCobertura}
        <Alert
          icon={<CheckCircleOutlineIcon fontSize="inherit" />}
          severity={dados.partial ? 'info' : 'success'}
          sx={{ mt: 2 }}
        >
          <Typography sx={{ fontWeight: 600 }}>
            Nenhuma mudança detectada
            {dados.partial ? ` nos ${dados.hosts_compared} host(s) que responderam` : ''}
          </Typography>
          <Typography variant="body2">
            Comparação entre a {antes} e a {depois}.
            Os campos voláteis (uptime, contadores, dead-timer) foram ignorados.
          </Typography>
        </Alert>
      </>
    );
  }

  return (
    <Box sx={{ mt: 2 }}>
      {avisoCobertura}

      {/* Resumo por tipo de evento, clicável como filtro. */}
      <Paper variant="outlined" sx={{ p: 1.5, mb: 2, ...SUPERFICIE }}>
        <Typography variant="caption" sx={{ color: '#64748b' }}>
          {dados.total_events} evento(s) entre a {antes} e a {depois}
        </Typography>
        <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
          {ORDEM_EVENTO.map((tipo) => {
            const qtd = dados.by_event?.[tipo] || 0;
            if (!qtd) return null;
            const cfg = EVENTO[tipo];
            const ativo = filtroEvento === tipo;
            return (
              <Tooltip key={tipo} title={cfg.desc}>
                <Chip
                  label={`${cfg.sym} ${cfg.label}: ${qtd}`}
                  onClick={() => setFiltroEvento(ativo ? null : tipo)}
                  sx={{
                    bgcolor: ativo ? cfg.cor : '#fff',
                    color: ativo ? '#fff' : cfg.cor,
                    border: `1px solid ${cfg.cor}`,
                    fontWeight: 600,
                  }}
                />
              </Tooltip>
            );
          })}
        </Stack>

        <Divider sx={{ my: 1.5 }} />

        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {Object.entries(dados.by_category || {}).map(([cat, contagens]) => {
            const total = Object.values(contagens).reduce((a, b) => a + b, 0);
            if (!total) return null;
            const ativo = filtroCategoria === cat;
            const cor = CAT_COLOR[cat] || CAT_COLOR.OUTROS;
            return (
              <Chip
                key={cat}
                size="small"
                label={`${cat}: ${total}`}
                onClick={() => setFiltroCategoria(ativo ? null : cat)}
                sx={{
                  bgcolor: ativo ? cor : '#fff',
                  color: ativo ? '#fff' : cor,
                  border: `1px solid ${cor}`,
                }}
              />
            );
          })}
        </Stack>
      </Paper>

      {/* Host com mais mudança primeiro — é por onde o operador quer começar. */}
      {hostsFiltrados.map((h) => (
        <Accordion key={h.host_id} defaultExpanded={hostsFiltrados.length <= 3} sx={SUPERFICIE}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ width: '100%' }}>
              <Typography sx={{ fontWeight: 600 }}>
                {hostsPorId[h.host_id]?.host_name || `Host ${h.host_id}`}
              </Typography>
              <Typography variant="caption" sx={{ color: '#64748b' }}>
                {hostsPorId[h.host_id]?.ip_address || ''}
              </Typography>
              <Box sx={{ flex: 1 }} />
              <Stack direction="row" spacing={0.5}>
                {ORDEM_EVENTO.map((tipo) => {
                  const qtd = h.events.filter((e) => e.event === tipo).length;
                  if (!qtd) return null;
                  return (
                    <Box
                      key={tipo}
                      sx={{
                        color: EVENTO[tipo].cor, fontWeight: 700, fontSize: 13,
                        minWidth: 28, textAlign: 'right',
                      }}
                    >
                      {EVENTO[tipo].sym}{qtd}
                    </Box>
                  );
                })}
              </Stack>
            </Stack>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0 }}>
            {h.events.map((ev, i) => (
              <Evento key={`${ev.entity_type}-${ev.entity_key}-${i}`} ev={ev} />
            ))}
          </AccordionDetails>
        </Accordion>
      ))}

      {hostsFiltrados.length === 0 && (
        <Alert severity="info">Nenhum evento com os filtros selecionados.</Alert>
      )}
    </Box>
  );
}
